import { useEffect, useRef, useState, type ReactNode } from 'react'
import { checkOutreachText, type StyleIssue } from '../../lib/adminClient'

// Small pieces the Prospects page is built from. Plain and quiet on purpose:
// the page is read every day, so it should be easy on the eye and say only
// what the reader needs to act.

export function Card({ title, aside, children, testId, tone }: { title?: ReactNode; aside?: ReactNode; children: ReactNode; testId?: string; tone?: 'accent' | 'warn' }): JSX.Element {
  const border = tone === 'accent' ? 'border border-accent/30' : tone === 'warn' ? 'border border-amber-400/30' : ''
  return (
    <section className={`glass-card rounded-xl p-5 ${border}`} data-testid={testId}>
      {(title || aside) && (
        <div className="flex items-start justify-between gap-3 mb-3">
          {title && <h3 className="text-[11px] uppercase tracking-[0.16em] text-slate-400">{title}</h3>}
          {aside}
        </div>
      )}
      {children}
    </section>
  )
}

export function Pill({ children, tone = 'muted', testId }: { children: ReactNode; tone?: 'muted' | 'good' | 'warn' | 'bad' | 'accent'; testId?: string }): JSX.Element {
  const cls = {
    muted: 'bg-white/5 text-slate-300',
    good: 'bg-emerald-500/15 text-emerald-300',
    warn: 'bg-amber-500/15 text-amber-200',
    bad: 'bg-red-500/15 text-red-300',
    accent: 'bg-accent/15 text-accent'
  }[tone]
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] ${cls}`} data-testid={testId}>
      {children}
    </span>
  )
}

const URL_RE = /(https?:\/\/[^\s<>"')\]|]+)/g

/** Text with its web addresses turned into links that open in a new tab. */
export function Linkify({ text }: { text: string | null }): JSX.Element {
  if (!text) return <span className="text-slate-500">Not recorded</span>
  const parts = text.split(URL_RE)
  return (
    <>
      {parts.map((p, i) =>
        i % 2 === 1 ? (
          <a key={i} href={p.replace(/[.,;:!?]+$/, '')} target="_blank" rel="noreferrer" className="text-accent hover:underline break-all">
            {shortUrl(p.replace(/[.,;:!?]+$/, ''))}
          </a>
        ) : (
          <span key={i}>{p}</span>
        )
      )}
    </>
  )
}

export function shortUrl(u: string): string {
  try {
    const x = new URL(u)
    const path = x.pathname.length > 32 ? `${x.pathname.slice(0, 30)}…` : x.pathname
    return `${x.hostname.replace(/^www\./, '')}${path === '/' ? '' : path}`
  } catch {
    return u
  }
}

/**
 * Copy text, then open a page. The copy happens first and inside the click,
 * which is the only time a browser allows it; the page opens once the text is
 * on the clipboard, so whatever is pasted is what was shown.
 */
export async function copyThenOpen(text: string, url: string | null): Promise<'copied' | 'copy_failed'> {
  let result: 'copied' | 'copy_failed' = 'copied'
  try {
    await navigator.clipboard.writeText(text)
  } catch {
    result = 'copy_failed'
  }
  if (url) window.open(url, '_blank', 'noopener')
  return result
}

/** The style check, live, as the text is edited (server: outreachStyle.ts). */
export function useStyleCheck(text: string, kind: 'note' | 'dm' | 'email', name: string): StyleIssue[] | null {
  const [issues, setIssues] = useState<StyleIssue[] | null>(null)
  const seq = useRef(0)
  useEffect(() => {
    const n = ++seq.current
    const t = setTimeout(() => {
      void checkOutreachText(text, kind, name).then((r) => {
        if (n === seq.current && r.ok) setIssues(r.data.issues)
      })
    }, 350)
    return () => clearTimeout(t)
  }, [text, kind, name])
  return issues
}

export function StyleIssues({ issues, testId }: { issues: StyleIssue[] | null; testId?: string }): JSX.Element | null {
  if (!issues) return null
  if (issues.length === 0) {
    return (
      <div className="text-[11px] text-emerald-300/80" data-testid={testId}>
        Reads like a person wrote it. Still read it once before sending.
      </div>
    )
  }
  return (
    <ul className="text-[11px] space-y-0.5" data-testid={testId}>
      {issues.map((i, k) => (
        <li key={k} className={i.level === 'block' ? 'text-red-300' : 'text-amber-200'}>
          {i.level === 'block' ? 'Must fix: ' : ''}
          {i.found ? <span className="font-medium">“{i.found}”</span> : null} {i.why}
        </li>
      ))}
    </ul>
  )
}

export function Counter({ n, max }: { n: number; max: number }): JSX.Element {
  return <span className={`text-[11px] tabular-nums ${n > max ? 'text-red-300' : n > max * 0.9 ? 'text-amber-200' : 'text-slate-500'}`}>{n} / {max}</span>
}

export const inputCls = 'w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-accent'
export const selectCls = 'bg-black/30 border border-white/10 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:border-accent'

export function when(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  const days = Math.floor((Date.now() - d.getTime()) / 86_400_000)
  const date = d.toISOString().slice(0, 10)
  return days <= 0 ? `today (${date})` : days === 1 ? `yesterday (${date})` : days < 30 ? `${days} days ago (${date})` : date
}
